/**
 * EmployeeScheduleLegend.tsx
 * Compact colour key for the live Employee Schedule (Matrix / Board / Timeline).
 *
 * The Employee Schedule previously had NO legend on screen — the only one lived in
 * an unused toolbar component (deleted in the IMP2-1 dead-code sweep). This
 * is a small, always-visible key wired into the live page. Bar colours come from
 * the same resolver the bars use (employeeBarColor) so the swatches can't drift;
 * the capacity bands match CapacityHeatmapCell, and the per-day overbook warning
 * glyph mirrors the red ⚠ the cells render.
 */

import React, { useState } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material/styles';
import { employeeBarColor } from '../../../components/schedule/employeeScheduleShared';
import { capacityHeatColor, capacityHeatGradientCss } from '../../../utils/scheduleCapacity';
import type { EmployeeBarKind } from '../../../services/scheduleEmployeeBars';

const BAR_KINDS: { kind: EmployeeBarKind; label: string }[] = [
  { kind: 'Task', label: 'Task' },
  { kind: 'Vacation', label: 'Vacation' },
  { kind: 'Sick', label: 'Sick' },
  { kind: 'FieldWork', label: 'Field Work' },
  { kind: 'Other', label: 'Other' },
];

const Swatch: React.FC<{ color: string; borderColor: string; round?: boolean }> = ({ color, borderColor, round }) => (
  <Box
    aria-hidden="true"
    sx={{
      width: 12,
      height: 12,
      borderRadius: round ? '50%' : '3px',
      backgroundColor: color,
      border: `1px solid ${borderColor}`,
      flexShrink: 0,
    }}
  />
);

const Item: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    {children}
    <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
      {label}
    </Typography>
  </Box>
);

/** Collapsed/expanded preference — remembered per the designer pass (2026-07-26). */
const LEGEND_OPEN_KEY = 'employeeSchedule.legendOpen';

export const EmployeeScheduleLegend: React.FC = () => {
  const theme = useTheme();
  const border = theme.border.color;
  const [open, setOpen] = useState<boolean>(() => localStorage.getItem(LEGEND_OPEN_KEY) !== '0');
  const toggle = () => setOpen((o) => { localStorage.setItem(LEGEND_OPEN_KEY, o ? '0' : '1'); return !o; });

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        columnGap: 1.25,
        rowGap: 0.25,
        px: 0.5,
        py: 0.25,
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
        aria-expanded={open}
        title={open ? 'Hide the colour key' : 'Show the colour key'}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.25, cursor: 'pointer', borderRadius: '4px', px: 0.5, '&:hover': { backgroundColor: theme.palette.action.hover } }}
      >
        <Typography
          variant="caption"
          fontWeight={700}
          color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6rem' }}
        >
          Legend
        </Typography>
        <ExpandMoreIcon sx={{ fontSize: 14, color: 'text.secondary', transition: 'transform 150ms', transform: open ? 'none' : 'rotate(-90deg)' }} />
      </Box>

      {open && (
        <>
      {BAR_KINDS.map(({ kind, label }) => (
        <Item key={kind} label={label}>
          <Swatch color={employeeBarColor(theme, kind)} borderColor={border} />
        </Item>
      ))}

      <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

      <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.66rem', color: 'text.secondary' }}>
        Capacity:
      </Typography>
      {/* Weekly cell = average daily load across the week's working days
          (BUG-2026-0196: a 10% reviewer reads 10%, not "every day busy") —
          lighter = light share / few days, darker = fully loaded all week. */}
      <Item label="Light → fully loaded">
        <Box
          aria-hidden="true"
          sx={{
            width: 64,
            height: 12,
            borderRadius: '3px',
            backgroundImage: capacityHeatGradientCss(),
            border: `1px solid ${border}`,
            flexShrink: 0,
          }}
        />
      </Item>
      {/* Conflict (Q3B) — a task assigned on a day the person has a day off.
          The one remaining hard signal (red ring + ⚠). */}
      <Item label="Conflict — assigned on a day off">
        <Box
          aria-hidden="true"
          sx={{
            width: 12,
            height: 12,
            borderRadius: '3px',
            backgroundColor: capacityHeatColor(160).bg,
            boxShadow: `inset 0 0 0 2px ${theme.schedule.heatmapHigh}`,
            flexShrink: 0,
          }}
        />
      </Item>
      {/* Weekly overload advisory (non-blocking, amber ring): more task-days
          than the week holds — several tasks stacked on the same tech. */}
      <Item label="Overloaded — more task-days than the week holds">
        <Box
          aria-hidden="true"
          sx={{
            width: 12,
            height: 12,
            borderRadius: '3px',
            backgroundColor: capacityHeatColor(100).bg,
            boxShadow: `inset 0 0 0 2px ${theme.schedule.maintenance}`,
            flexShrink: 0,
          }}
        />
      </Item>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

      {/* Staffing Allocation % bands (D5) — the top pill in each week tile: the
          person's Σ allocation share for the week (over 100% warns, never blocks). */}
      <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.66rem', color: 'text.secondary' }}>
        Utilization:
      </Typography>
      <Item label="Under (<50%)"><Swatch color={theme.schedule.allocUnder} borderColor={border} /></Item>
      <Item label="Healthy (50–85%)"><Swatch color={theme.schedule.allocHealthy} borderColor={border} /></Item>
      <Item label="At capacity (86–100%)"><Swatch color={theme.schedule.allocAt} borderColor={border} /></Item>
      <Item label="Over (>100%)"><Swatch color={theme.schedule.allocOver} borderColor={border} /></Item>
        </>
      )}
    </Box>
  );
};
